import React from "react";
import Markdown from "markdown-to-jsx";
import {
    Box,
    Code,
    Divider,
    Heading,
    Link,
    ListItem,
    OrderedList,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    UnorderedList,
    useColorModeValue,
} from "@chakra-ui/react";
import ChartBlock from "./ChartBlock";

// Fenced ```wschart blocks carry a JSON chart spec — markdown-to-jsx nests the
// language as a `lang-wschart`/`language-wschart` class on the inner <code>.
const CHART_LANG = /(?:lang|language)-wschart\b/i;

// Flattens a React node tree back to its raw text (the fenced code content).
function nodeText(node) {
    if (node == null) return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(nodeText).join("");
    if (node.props && node.props.children != null) return nodeText(node.props.children);
    return "";
}

/**
 * Code blocks (<pre><code>) render inside a scrollable monospaced box, while
 * inline code keeps the compact Chakra <Code> look. A ```wschart fence is
 * hijacked to render an inline chart instead.
 */
const Pre = (props) => {
    const bg = useColorModeValue("blackAlpha.100", "whiteAlpha.100");

    const child = React.Children.toArray(props.children)[0];
    const className = (child && child.props && child.props.className) || "";
    if (CHART_LANG.test(className)) {
        return <ChartBlock raw={nodeText(child.props.children)} />;
    }

    return (
        <Box
            as="pre"
            fontSize="xs"
            fontFamily="mono"
            bg={bg}
            borderRadius="md"
            p={3}
            my={2}
            overflowX="auto"
            {...props}
        />
    );
};

const TableWrapper = (props) => (
    <Box overflowX="auto" my={2}>
        <Table size="sm" variant="simple" {...props} />
    </Box>
);

// Only allow safe href schemes in model-provided links. Anything else
// (javascript:, data:, etc.) renders as plain text instead of a clickable link.
const SAFE_HREF = /^(https?:\/\/|mailto:|\/|#|\.)/i;
const SafeLink = ({ href, children, ...props }) => {
    const safe = typeof href === "string" && SAFE_HREF.test(href.trim());
    if (!safe) {
        return <Text as="span" {...props}>{children}</Text>;
    }
    return (
        <Link href={href} color="brand.500" isExternal textDecoration="underline" {...props}>
            {children}
        </Link>
    );
};

const overrides = {
    p: { component: Text, props: { fontSize: "sm", mb: 2, lineHeight: "tall" } },
    h1: { component: Heading, props: { as: "h1", size: "md", mt: 3, mb: 2 } },
    h2: { component: Heading, props: { as: "h2", size: "sm", mt: 3, mb: 2 } },
    h3: { component: Heading, props: { as: "h3", size: "xs", mt: 2, mb: 1, textTransform: "uppercase", letterSpacing: "wide" } },
    ul: { component: UnorderedList, props: { fontSize: "sm", pl: 4, mb: 2, spacing: 1 } },
    ol: { component: OrderedList, props: { fontSize: "sm", pl: 4, mb: 2, spacing: 1 } },
    li: { component: ListItem, props: { fontSize: "sm" } },
    a: { component: SafeLink },
    code: { component: Code, props: { fontSize: "xs", colorScheme: "gray" } },
    pre: { component: Pre },
    table: { component: TableWrapper },
    thead: { component: Thead },
    tbody: { component: Tbody },
    tr: { component: Tr },
    th: { component: Th, props: { fontSize: "xs" } },
    td: { component: Td, props: { fontSize: "xs" } },
    hr: { component: Divider, props: { my: 3 } },
    blockquote: {
        component: Box,
        props: {
            as: "blockquote",
            borderLeftWidth: "3px",
            borderColor: "brand.300",
            pl: 3,
            my: 2,
            fontSize: "sm",
            fontStyle: "italic",
            opacity: 0.9,
        },
    },
};

export default function MarkdownRenderer({ children }) {
    if (!children) return null;

    return (
        <Markdown options={{ overrides, forceBlock: true, disableParsingRawHTML: true }}>
            {String(children)}
        </Markdown>
    );
}
